import { useSyncExternalStore, type ReactNode } from 'react';
import { usePermissions } from 'react-admin';
import {
  hasAnyPermission,
  hasPermission,
  isElementHidden,
  type PermissionKey,
} from './permissions';
import { getRoleAccess, subscribeRoleAccess } from './roleCenter/roleAccessStore';

/**
 * Hak i komponent do ukrywania sekcji bez uprawnienia (role działowe,
 * 01.10.2026). Rola zalogowanego pochodzi z `authProvider.getPermissions()`
 * — tego samego źródła co menu; reguła w `permissions.ts`.
 */

/**
 * Stan centrum ról (22.10.2026) — hak przelicza widok, gdy stan przyjdzie
 * albo się zmieni (np. po zmianie wersji konta).
 */
export function useRoleAccessState() {
  return useSyncExternalStore(subscribeRoleAccess, getRoleAccess);
}

const useRoleAccessVersion = useRoleAccessState;

/** Czy zalogowany ma uprawnienie. Zanim rola przyjdzie — nie. */
export function useHasPermission(permission: PermissionKey): boolean {
  const { permissions } = usePermissions<string>();
  useRoleAccessVersion();
  return hasPermission(permissions, permission);
}

/** Czy zalogowany ma którekolwiek z uprawnień. Zanim rola przyjdzie — nie. */
export function useHasAnyPermission(permissions: readonly PermissionKey[]): boolean {
  const { permissions: role } = usePermissions<string>();
  useRoleAccessVersion();
  return hasAnyPermission(role, permissions);
}

/**
 * Czy element interfejsu jest widoczny dla zalogowanego według centrum ról
 * (22.10.2026, „Co widzi”). Bez centrum — tak (decydują same uprawnienia).
 * Ukrycie niczego nie odbiera: o dostępie rozstrzyga API.
 */
export function useElementVisible(element: string): boolean {
  const { permissions: role } = usePermissions<string>();
  useRoleAccessVersion();
  return !isElementHidden(role, element);
}

/**
 * Sekcja widoczna tylko z uprawnieniem. Bez niego — nic (nie wyszarzenie,
 * nie komunikat): planista nie ma oglądać sekcji Trade, a trader floty.
 * Lista uprawnień = wystarczy dowolne z nich.
 *
 * `fallback` — dla całych ekranów otwieranych z adresu (karta pojazdu,
 * 06.10.2026): zamiast pustej strony komunikat o braku dostępu. Pokazujemy
 * go dopiero, gdy rola jest znana — inaczej mignąłby komuś, kto dostęp ma.
 */
export const PermissionGate = ({
  permission,
  element,
  children,
  fallback = null,
}: {
  /** Pominięte = bez wymogu uprawnienia (sam element z centrum ról). */
  permission?: PermissionKey | readonly PermissionKey[];
  /**
   * Element interfejsu z centrum ról (22.10.2026, „Co widzi”;
   * `roleCenter/elementKeys.ts`) — ukryty dla roli = jak bez uprawnienia.
   */
  element?: string;
  children: ReactNode;
  fallback?: ReactNode;
}) => {
  const { permissions: role, isPending } = usePermissions<string>();
  useRoleAccessVersion();
  const allowed =
    (permission === undefined ||
      hasAnyPermission(role, typeof permission === 'string' ? [permission] : permission)) &&
    (element === undefined || !isElementHidden(role, element));
  if (allowed) return <>{children}</>;
  return isPending ? null : <>{fallback}</>;
};
